/**
 * TransferCoinPage — admin tool for transferring tokens to a user (§2.14 给用户转币).
 *
 * Two tabs:
 *   1. Transfer form (two-phase OperatorForm)
 *   2. Transfer log (DataTable filtered to action=admin_transfer_coin)
 */
import { useState } from 'react';
import { Descriptions, Tabs, Tag, InputNumber, Space, Select, App } from 'antd';
import { OperatorForm } from '../../components/OperatorForm';
import { DataTable } from '../../components/DataTable';
import { useAuthStore } from '../../store/auth';
import { TOKEN_IDS, TOKEN_META, type TokenId } from '@uo/shared';

interface TransferPreview {
  user_id: number;
  username: string;
  asset: string;
  amount: string;
  balance_before: string;
  balance_after: string;
  would_apply?: boolean;
}

interface TransferLogRow {
  id: string;
  user_id: number;
  asset: string;
  action: string;
  delta: string;
  balance_after: string;
  note: string | null;
  operator_id: number | null;
  created_at: string;
}

export default function TransferCoinPage() {
  const { message } = App.useApp();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canTransfer = hasNode('platform.user.transfer_coin');

  // Log filters
  const [logUserId, setLogUserId] = useState<number | undefined>(undefined);
  const [logAsset, setLogAsset] = useState<string | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);

  const logColumns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '用户ID', dataIndex: 'user_id', width: 80 },
    {
      title: '资产',
      dataIndex: 'asset',
      width: 80,
      render: (v: string) => <Tag color="blue">{(TOKEN_META as any)[v]?.symbol ?? v}</Tag>,
    },
    { title: '数量', dataIndex: 'delta', width: 120 },
    { title: '转后余额', dataIndex: 'balance_after', width: 140 },
    { title: '备注', dataIndex: 'note', width: 200, ellipsis: true },
    { title: '操作员', dataIndex: 'operator_id', width: 80 },
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
  ];

  return (
    <Tabs
      defaultActiveKey="transfer"
      items={[
        {
          key: 'transfer',
          label: '转币给用户',
          children: canTransfer ? (
            <div style={{ maxWidth: 480 }}>
              <OperatorForm<TransferPreview>
                endpoint="/api/admin/transfer-coin"
                fields={[
                  {
                    name: 'user_id',
                    label: '目标用户 ID',
                    type: 'number',
                    required: true,
                    placeholder: '输入用户 ID',
                  },
                  {
                    name: 'asset',
                    label: '资产类型',
                    type: 'select',
                    required: true,
                    options: TOKEN_IDS.map((t: TokenId) => ({
                      value: t,
                      label: `${TOKEN_META[t].symbol} — ${TOKEN_META[t].name}`,
                    })),
                  },
                  {
                    name: 'amount',
                    label: '转入数量（正数）',
                    type: 'text',
                    required: true,
                    placeholder: '如 100.5',
                  },
                  {
                    name: 'note',
                    label: '转币备注',
                    type: 'textarea',
                    required: true,
                    placeholder: '说明转币原因',
                  },
                ]}
                renderPreview={(p) => (
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="用户">{p.username} (ID: {p.user_id})</Descriptions.Item>
                    <Descriptions.Item label="资产">{p.asset.toUpperCase()}</Descriptions.Item>
                    <Descriptions.Item label="转入数量">
                      <strong style={{ color: '#52c41a' }}>+{p.amount}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="转前余额">{p.balance_before}</Descriptions.Item>
                    <Descriptions.Item label="转后余额">
                      <strong>{p.balance_after}</strong>
                    </Descriptions.Item>
                  </Descriptions>
                )}
                onDone={() => {
                  message.success('转币成功');
                  setRefreshKey((k) => k + 1);
                }}
              />
            </div>
          ) : (
            <p style={{ color: '#999' }}>当前账号无转币权限</p>
          ),
        },
        {
          key: 'log',
          label: '转币记录',
          children: (
            <div>
              <Space style={{ marginBottom: 16 }}>
                <InputNumber
                  placeholder="用户ID"
                  min={1}
                  value={logUserId}
                  onChange={(v) => setLogUserId(v ?? undefined)}
                  style={{ width: 120 }}
                />
                <Select
                  allowClear
                  placeholder="资产类型"
                  value={logAsset}
                  onChange={(v) => setLogAsset(v)}
                  style={{ width: 160 }}
                  options={TOKEN_IDS.map((t: TokenId) => ({
                    value: t,
                    label: TOKEN_META[t].symbol,
                  }))}
                />
              </Space>
              <DataTable<TransferLogRow>
                queryKey={['admin-transfer-coin-log', logUserId, logAsset, refreshKey]}
                endpoint="/api/admin/transfer-coin/log"
                extraParams={{ user_id: logUserId, asset: logAsset }}
                columns={logColumns}
                rowKey="id"
              />
            </div>
          ),
        },
      ]}
    />
  );
}
